import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Button,
  makeStyles,
  SwitchProps,
  TextField,
  styled,
  Switch,
  createStyles,
  Theme,
  Modal,
  useMediaQuery,
} from "@material-ui/core";
import theme from "../../../themes";
import defaultEnumStrings, {
  RedirectUri,
  useEnumStrings,
} from "../../enumStrings";
import {
  ChamberInput,
  ChamberPerksAppSubscriptionPlanTypesEnum,
  useDeleteChamberMutation,
  useGetAdminConsoleLoginEmailForChamberQuery,
  useGetChamberQuery,
  useGetPremiumChamberMarketerLinkQuery,
  useGetSubscriptionPlanTypeQuery,
  UserType,
  useUpdateChamberMutation,
} from "../../../graphql/types";
import * as yup from "yup";
import { useFormik } from "formik";
import { CircularProgress } from "@material-ui/core";
import ConfirmationModal, {
  ButtonConfigType,
  YesNoButtonConfig,
} from "../../common/ConfirmationModal/ConfirmationModal";
import { useLocation } from "react-router-dom";
import useRedirect from "../../common/Hooks/useRedirect";
import Loader from "../../common/Loader/Loader";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import LogoPicker from "../../common/utilities/LogoPicker";
import ProvincePicker from "../../common/utilities/ProvincePicker";
import { UserTypeContext } from "../../../UserTypeContext";
import PhoneNumberFormat from "../../common/utilities/PhoneNumberFormat";
import ChamberChangeEmailUsernameModal from "../../common/ConfirmationModal/ChamberChangeEmailUsernameModal";
import BannerUploadModal from "./BannerUploadModal";
import DeleteLogoModal from "./DeleteLogoModal";
import EditOrangeIcon from "../../../images/edit-icon-orange.png";
import UploadRoundIcon from "../../../images/upload-rounded.png";
import TrashIcon from "../../../images/trash.png";
import IdaChatbotIcon from "../../../images/ida-chatbot-icon.png";
import AIRewriteIcon from "../../../images/airewrite.png";
import IDAOverwriteDialog from "../../common/IDAOverwriteDialog";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import PlayVideoIcon from "../../../images/play-video-icon.svg";
import omitDeep from "omit-deep-lodash";
import { validWebsiteRegEx } from "../../common/utilities/regexes";
import ChamberSubscriptionPlanRadioGroup from "../../common/ChamberSubscriptionPlanRadioGroup";
import BannerPreviewModal from "./BannerPreviewModal";
import DeleteBannerModal from "./DeleteBannerModal";
import IDAGenerateModal from "../../common/IDAGenerateModal";
import SuccessDialog from "../../common/SuccessDialog";

const CHARACTER_LIMIT_DESCRIPTION = 2000;

const useStyles = makeStyles((themes: any) => ({
  root: {
    backgroundColor: theme.palette.delete.main,
    color: theme.palette.delete.contrastText,
    toolbar: theme.mixins.toolbar,
  },
  background: {},
  title: {
    fontSize: "32px",
    color: "white",
    fontWeight: "bold",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      fontSize: "22px",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "15px 40px",
    [theme.breakpoints.down("sm")]: {
      padding: "12px 16px",
    },
  },
  pageHeaderButtonRow: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > div": { width: "100%" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  contentContainer: {
    height: "100vh",
    backgroundColor: "#F6F6F6",
  },
  boxTitle: {
    fontWeight: "bold",
    fontSize: "4.3vh",
    textAlign: "center",
    marginTop: "11px",
  },
  boxValue: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    fontSize: "21vh",
    textAlign: "center",
  },
  buttonContainer: {
    display: "flex",
    paddingBottom: "9px",
    paddingLeft: "24px",
  },
  titleContent: {
    display: "flex",
    flexDirection: "column-reverse",
    [theme.breakpoints.up("lg")]: {
      paddingTop: "15px",
    },
    "@media (max-width: 768px)": {
      minHeight: "auto",
    },
  },

  images: {
    borderRadius: "200px",
    margin: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "85px",
      height: "85px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "140px",
      height: "140px",
    },
  },
  chamberTitle: {
    margin: "auto",
    alignItems: "center",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1em",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "2em",
    },
  },
  companyInputWithToolTipContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: "36px",
  },
  searchContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  chamberInput: {
    backgroundColor: "#F2F2F6",
    paddingBottom: "10px",
    borderRadius: "15px",
    border: "1px solid black",
    marginTop: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "55px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "55px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#000000",
      },
    },
    "& label.Mui-focused": {
      color: "#000000",
    },
    "& label.Mui": {
      color: "black",
    },
    "& .MuiSelect-select.MuiSelect-select": {
      paddingLeft: "10px",
    },
  },
  content: {
    background: "#FFFFFF",
    flexGrow: 1,
    borderRadius: "16px",
    margin: "20px",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
    },
    flex: "1",
    marginTop: "33px",
    paddingTop: "30px",
    paddingBottom: "30px",
  },
  buttonContainers: {
    marginTop: "46px",
    display: "flex",
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-around",
  },

  invalidError: {
    textAlign: "center",
    color: "red",
  },
  chamberInputLogo: {
    display: "flex",
    justifyContent: "flex-end",
    overflow: "hidden",
    padding: "8px",
    outline: "none",
    backgroundColor: "#F2F2F6",
    border: "1.55px solid black",
    borderRadius: "15px",
    paddingLeft: "10px",
    paddingTop: "10px",
    marginTop: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "100px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "100px",
    },
  },
  labelLogo: {
    display: "flex",
    flexDirection: "row-reverse",
    width: "100%",
    justifyContent: "space-between",
    color: "#9A9A9C",
    font: "inherit",
    fontSize: "1.18676em",
  },
  avatarImages: {
    borderRadius: "200px",
    margin: "auto",
    fontSize: "30px",
    backgroundColor: "#67b917",

    [theme.breakpoints.down("sm")]: {
      width: "85px",
      height: "85px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "140px",
      height: "140px",
    },
  },
  companyInput: {
    backgroundColor: "#F2F2F6",
    paddingBottom: "10px",
    borderRadius: "15px",
    marginTop: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "55px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "55px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#000000",
      },
    },
    "& label.Mui-focused": {
      color: "#000000",
    },
    "& label.Mui": {
      color: "black",
    },
  },
  selectedValue: {
    "& .MuiSelect-select:focus": {
      background: "transparent",
    },
    "& .MuiSelect-icon": {
      height: "53px",
      backgroundColor: theme.palette.primary.main,
      top: "-16px",
      borderRadius: "0px 15px 15px 0px",
      width: "55px",
    },
    "& .MuiSelect-iconOpen": {
      height: "53px",
      backgroundColor: theme.palette.primary.main,
      top: "-16px",
      borderRadius: "0px 0px 0px 0px",
      width: "55px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
  },
  companyInputLogo: {
    display: "flex",
    justifyContent: "flex-end",
    overflow: "hidden",
    padding: "8px",
    outline: "none",
    backgroundColor: "#F2F2F6",
    border: "1.55px solid black",
    borderRadius: "15px",
    paddingLeft: "10px",
    paddingTop: "10px",
    marginTop: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "100px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "100px",
    },
  },
  companyDescription: {
    backgroundColor: "#F2F2F6",
    borderRadius: "15px",
    border: "1px solid black",
    marginTop: "50px",
    paddingLeft: "10px",
    paddingRight: "10px",
    [theme.breakpoints.down("sm")]: {
      width: "265px",
      height: "105px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
      height: "105px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "black",
        borderRadius: "15px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0000008a",
      },
    },
    "& label.Mui-focused": {
      color: "#0000008a",
    },
    "& select": {},
    "& label.Mui": {
      color: "black",
      paddingLeft: "10px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
  },
  companyDescriptionInput: {
    "& .MuiSelect-select:focus": {
      background: "transparent",
    },
    "& .MuiSelect-icon": {
      height: "53px",
      backgroundColor: theme.palette.primary.main,
      top: "-16px",
      borderRadius: "0px 7px 7px 0px",
      width: "55px",
    },
    "& .MuiSelect-iconOpen": {
      height: "53px",
      backgroundColor: theme.palette.primary.main,
      top: "-16px",
      borderRadius: "0px 0px 0px 0px",
      width: "55px",
    },
    "& .MuiPaper-root": {
      minWidth: "430px!important",
      top: "269px!important",
      left: "256px!important",
    },
    "& .MuiInputBase-inputMultiline": {
      overflow: "scroll !important",
      height: "79px!important",
    },
  },
  groupRoot: {
    [theme.breakpoints.down("sm")]: {
      width: "265px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "431px",
    },
  },
  // New sectioned layout styles
  formSection: {
    maxWidth: "1096px",
    margin: "50px auto 50px auto",
    padding: "48px 35px 35px 35px",
    border: "1px solid #9E9E9E",
    borderRadius: "16px",
    position: "relative",
    boxSizing: "border-box",
    overflow: "visible",
    backgroundColor: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      margin: "40px 12px 24px 12px",
      padding: "40px 16px 20px 16px",
      maxWidth: "calc(100% - 24px)",
    },
  },
  sectionHeader: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "30px",
    padding: "15px 35px",
    display: "inline-block",
    position: "absolute" as const,
    top: "-28px",
    left: "26px",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 18px",
      left: "12px",
      top: "-22px",
      maxWidth: "calc(100% - 24px)",
    },
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 700,
    margin: 0,
    lineHeight: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "18px",
    },
  },
  sectionHelperText: {
    color: "#000000",
    fontSize: "16px",
    lineHeight: "1.5",
    marginTop: "0px",
    marginBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "1.5",
      marginBottom: "14px",
    },
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  formRowSingle: {
    display: "grid",
    gridTemplateColumns: "1fr",
    marginBottom: "20px",
  },
  fieldLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    fontSize: "16px",
    color: "#000000",
    fontWeight: 600,
    marginBottom: "8px",
  },
  fieldLabelText: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  optionalText: {
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  sponsorLogoInput: {
    paddingBottom: "10px",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: theme.palette.text.primary,
        borderRadius: "8px",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#333333",
    },
  },
  requiredAsterisk: {
    color: "red",
  },
  sectionInput: {
    paddingBottom: "10px",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: theme.palette.text.primary,
        borderRadius: "8px",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& label.Mui-focused": {
      color: "#000000",
    },
    "& label.Mui": {
      color: "black",
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#333333",
    },
  },
  descriptionInput: {
    paddingBottom: "0px",
    borderRadius: "8px",
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      height: "100%",
      alignItems: "flex-start",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.23)",
        borderWidth: "1px",
        borderRadius: "8px",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.23)",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "2px",
      },
    },
    "& .MuiInputBase-root": {
      height: "100%",
    },
    "& .MuiInputBase-inputMultiline": {
      height: "100% !important",
      overflow: "auto !important",
    },
    "& label.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& label.Mui": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#333333",
    },
  },
  idaHelperContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    padding: "2px 5px",
    marginTop: "8px",
    backgroundColor: "#FFFFFF",
  },
  idaIcon: {
    width: "33px",
    height: "26px",
    objectFit: "contain" as const,
  },
  idaHelperText: {
    fontWeight: 400,
    fontSize: "12px",
    color: theme.palette.primary.main,
    lineHeight: "15px",
    "& b": {
      fontWeight: 700,
    },
  },
  idaGenerateButton: {
    backgroundColor: "#4CAF50",
    border: "1px solid #4CAF50",
    borderRadius: "10px",
    padding: "1px 13px",
    height: "17px",
    minWidth: "71px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#388E3C",
    },
  },
  idaGenerateButtonText: {
    fontWeight: 400,
    fontSize: "10px",
    color: "#FFFFFF",
    lineHeight: "15px",
  },
  // IDA Generate Dialog styles
  idaDialogPaper: {
    borderRadius: "20px",
    maxWidth: "520px",
    width: "520px",
    overflow: "hidden",
    padding: "0",
    backgroundColor: "#FFFFFF",
    position: "relative" as const,
  },
  idaDialogCloseButton: {
    position: "absolute" as const,
    right: "10px",
    top: "10px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "28px",
    height: "28px",
    padding: 0,
    zIndex: 1,
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  idaDialogMascotRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "4px",
    marginBottom: "8px",
    paddingLeft: "20px",
    paddingTop: "16px",
  },
  idaDialogSpeechBubble: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #ccc",
    borderRadius: "12px",
    padding: "6px 12px",
    fontSize: "12px",
    color: "#333",
    position: "relative" as const,
  },
  idaDialogMascotImg: {
    width: "80px",
    height: "80px",
    objectFit: "contain" as const,
  },
  idaDialogTitle: {
    fontWeight: 700,
    fontSize: "18px",
    color: theme.palette.primary.main,
    textAlign: "center" as const,
    lineHeight: "1.4",
    margin: "0 24px 8px 24px",
  },
  idaDialogSubtitle: {
    fontSize: "13px",
    color: "#333",
    textAlign: "center" as const,
    lineHeight: "1.5",
    margin: "0 24px 12px 24px",
  },
  idaDialogTextarea: {
    margin: "0 24px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "12px",
    fontSize: "13px",
    lineHeight: "1.6",
    resize: "vertical" as const,
    minHeight: "100px",
    outline: "none",
    "&:focus": {
      borderColor: theme.palette.primary.main,
    },
  },
  idaDialogGenerateBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "uppercase" as const,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "16px auto 20px auto",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
  },
  // Overwrite confirmation dialog
  overwriteDialogPaper: {
    borderRadius: "20px",
    maxWidth: "600px",
    width: "600px",
    position: "relative" as const,
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  overwriteCloseButton: {
    position: "absolute" as const,
    right: "12px",
    top: "12px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "30px",
    height: "30px",
    padding: 0,
    zIndex: 1,
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  overwriteIconCircle: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
  },
  overwriteTitle: {
    fontWeight: 700,
    fontSize: "20px",
    textAlign: "center" as const,
    color: "#000",
    marginBottom: "8px",
  },
  overwriteDesc: {
    fontSize: "14px",
    textAlign: "center" as const,
    color: "#333",
    lineHeight: "1.6",
    marginBottom: "16px",
    padding: "0 20px",
  },
  overwritePreviewBox: {
    border: `1px solid ${theme.palette.success.main}`,
    borderRadius: "6px",
    padding: "12px 6px",
    margin: "0 50px",
    maxHeight: "200px",
    overflow: "auto",
  },
  overwritePreviewLabel: {
    fontSize: "13px",
    fontWeight: 700,
    color: theme.palette.success.main,
    marginBottom: "8px",
  },
  overwritePreviewText: {
    fontSize: "13px",
    color: "#333",
    lineHeight: "1.6",
  },
  overwriteButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    margin: "20px 0 24px 0",
  },
  overwriteRegenBtn: {
    backgroundColor: theme.palette.success.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 24px",
    textTransform: "uppercase" as const,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&:hover": {
      backgroundColor: "#009A1B",
    },
  },
  overwriteConfirmBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "uppercase" as const,
    "&:hover": {
      backgroundColor: "#C99028",
    },
  },
  updateButton: {
    backgroundColor: theme.palette.primary.main,
    fontSize: "24px",
    fontWeight: "bold",
    borderRadius: "12px",
    padding: "15px 96px",
    color: "white",
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: "#D4A353",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
      padding: "14px 32px",
      width: "100%",
      maxWidth: "320px",
    },
  },
  bannerHelperText: {
    color: "#000000",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    marginBottom: "15px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  bannersGrid: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "20px",
    marginTop: "20px",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px 12px",
      alignItems: "stretch",
      justifyContent: "stretch",
      width: "100%",
    },
  },
  bannerCard: {
    backgroundColor: "#FFFFFF",
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    width: "209px",
    height: "175px",
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "175px",
    },
  },
  bannerCardEmpty: {
    backgroundColor: "#FFFFFF",
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    width: "182px",
    height: "114px",
    boxSizing: "border-box" as const,
    cursor: "pointer",
    padding: "22px 12px",
    gap: "10px",
    "&:hover": {
      backgroundColor: "#FFF3D6",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "90px",
      padding: "12px 8px",
      gap: "8px",
    },
  },
  bannerImageContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative" as const,
    padding: "12px",
    flex: 1,
  },
  bannerImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    borderRadius: "4px",
  },
  videoPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "4px",
    color: theme.palette.primary.main,
    gap: "4px",
  },
  bannerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 12px",
    backgroundColor: "transparent",
  },
  previewBannersButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#808080",
    color: "white",
    borderRadius: "12px",
    padding: "15px 96px",
    fontSize: "20px",
    fontWeight: 700,
    textTransform: "none" as const,
    marginTop: "30px",
    "&:hover": {
      backgroundColor: "#6B6B6B",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: "14px 24px",
      fontSize: "16px",
      marginTop: "16px",
    },
  },
  editDeleteButtons: {
    display: "flex",
    gap: "8px",
  },
  editButton: {
    backgroundColor: "#E3E3E3",
    borderRadius: "5px",
    width: "36px",
    height: "33px",
    minWidth: "36px",
    padding: "4px",
    "&:hover": {
      backgroundColor: "#D0D0D0",
    },
  },
  trashButton: {
    backgroundColor: (theme.palette as any).delete.main,
    borderRadius: "5px",
    width: "36px",
    height: "33px",
    minWidth: "36px",
    padding: "4px",
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  arrowButtons: {
    display: "flex",
    gap: "4px",
  },
  arrowButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: "4px",
    borderRadius: "5px",
    minWidth: "29px",
    width: "29px",
    height: "26px",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
    "&.Mui-disabled": {
      backgroundColor: "#D9D9D9",
      color: "#666",
    },
  },
  previewButton: {
    backgroundColor: "#333",
    color: "white",
    fontSize: "16px",
    fontWeight: "bold",
    borderRadius: "25px",
    padding: "12px 40px",
    textTransform: "none" as const,
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#444",
    },
  },
}));
const convertToBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

const ShowInAppSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  margin: "10px",
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,

    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
  "& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
    backgroundColor: theme.palette.primary.main,
    border: "1px solid black",
  },
  "& .MuiSwitch-root": {
    margin: "10px",
  },
}));

// Internal modal component for plan limitation
const PlanLimitationModal = ({
  open,
  handleClose,
  chamberName,
}: {
  open: boolean;
  handleClose: () => void;
  chamberName: string;
}) => {
  const modalStyles = makeStyles((theme: Theme) =>
    createStyles({
      modal: {
        border: "unset",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      paper: {
        height: "fit-content",
        borderRadius: "5px",
        textAlign: "center",
        width: "600px",
        maxWidth: "90%",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        backgroundColor: "#cfd8dc",
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        position: "relative",
      },
      okButton: {
        margin: "10px",
        marginTop: "20px",
        width: "120px",
        backgroundColor: "#37474f",
        color: "#e9e9e9",
        fontWeight: "bold",
        height: "40px",
      },
      content: {
        display: "flex",
        padding: "10px",
        borderRadius: "15px",
        textAlign: "left",
      },
    })
  )();

  const enumStrings = useEnumStrings();

  return (
    <Modal className={modalStyles.modal} open={open}>
      <div className={modalStyles.paper}>
        <h2 style={{ fontWeight: "bold" }}>
          {
            enumStrings.ChamberAdminManage
              .directoryBannersSubscriptionPlanLimitationModal.title
          }
        </h2>
        <div className={modalStyles.content}>
          <p style={{ fontSize: "18px", lineHeight: "1.5" }}>
            {enumStrings.ChamberAdminManage.directoryBannersSubscriptionPlanLimitationModal.description.replace(
              "{chamberName}",
              chamberName
            )}{" "}
            <a href={`mailto:${defaultEnumStrings.subscriptionsContactEmail}`}>
              {defaultEnumStrings.subscriptionsContactEmail}
            </a>
            .
          </p>
        </div>
        <Button
          className={modalStyles.okButton}
          onClick={handleClose}
          variant="contained"
        >
          {
            enumStrings.ChamberAdminManage
              .directoryBannersSubscriptionPlanLimitationModal.okButton
          }
        </Button>
      </div>
    </Modal>
  );
};

const ManageChamberAsChamberAdmin = (): ReactElement => {
  const {
    state: { userType },
  } = useContext(UserTypeContext);

  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [invalidError, setInvalidError] = useState(false);
  const [open, setOpen] = useState(false);

  // IDA Generate states
  const [idaDialogOpen, setIdaDialogOpen] = useState(false);
  const [idaKeyDetails, setIdaKeyDetails] = useState("");
  const [idaGeneratedText, setIdaGeneratedText] = useState("");
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [idaSuccessDialogOpen, setIdaSuccessDialogOpen] = useState(false);
  const [descriptionGeneratedByIda, setDescriptionGeneratedByIda] = useState(false);
  const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false);
  const location = useLocation();
  const locationStateChamberId = useMemo(() => {
    return (
      (location.state || { chamberId: undefined }) as {
        chamberId: string | undefined;
      }
    ).chamberId;
  }, [location.state]);

  // this is to get chamber when navigating from chamber list while loggged in as root admin
  const { data: locationStateChamberInfo } = useGetChamberQuery({
    variables: { chamberId: locationStateChamberId || "" },
    skip: !locationStateChamberId,
  });

  const {
    data: premiumChamberMarketerLinkData,
    loading: loadingPremiumChamberMarketerLinkData,
  } = useGetPremiumChamberMarketerLinkQuery({
    variables: { chamberId: locationStateChamberId || "" },
    skip: !locationStateChamberId || !(userType === UserType.RootAdmin),
  });

  const { data: subscriptionPlanTypeData } = useGetSubscriptionPlanTypeQuery({
    variables: { chamberId: locationStateChamberId || "" },
    skip: !locationStateChamberId || !(userType === UserType.RootAdmin),
  });

  // this is to get chamber when logged in as chamber admin and we can just pull info using logged in user's info
  const userIdInAuthChamberInfo = useGetChamberFromUserIdInAuth();
  const chamberInfo = useMemo(() => {
    if (locationStateChamberId) {
      return locationStateChamberInfo?.getChamber;
    }
    return userIdInAuthChamberInfo;
  }, [
    locationStateChamberId,
    locationStateChamberInfo,
    userIdInAuthChamberInfo,
  ]);
  const [customError, setCustomError] = useState("");

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };
  const redirectToManageChamber = useRedirect(RedirectUri.listAllChamberAdmin);

  const [deleteChamber] = useDeleteChamberMutation({
    variables: { id: chamberInfo?.id || "" },
  });

  const adminConsoleLoginEmailForChamber =
    useGetAdminConsoleLoginEmailForChamberQuery({
      variables: {
        chamberId: locationStateChamberId || "",
      },
      skip: !locationStateChamberId || !(userType === UserType.RootAdmin),
    });

  const yesButtonAction = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      await deleteChamber();
      redirectToManageChamber();
    } catch (error) {
      setInvalidError(true);
      setCustomError(error?.message);
      setLoader(false);
      setButtonDisabled(false);
    }

    setOpen(false);
  }, [deleteChamber, redirectToManageChamber]);

  const buttonConfig: YesNoButtonConfig = useMemo(() => {
    return {
      buttonType: ButtonConfigType.YesNoConfig,
      noButtonAction: handleClose,
      yesButtonAction,
    };
  }, [yesButtonAction]);

  const [updateChamber] = useUpdateChamberMutation();

  const validationSchema = yup.object({
    name: yup.string().required(enumStrings.validationMessages.nameRequired),
    description: yup.string().max(CHARACTER_LIMIT_DESCRIPTION, enumStrings.validationMessages.descriptionMaxCharacters),
    website: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite)
      .required(enumStrings.validationMessages.websiteRequired),
    address: yup.object({
      province: yup
        .string()
        .required(enumStrings.validationMessages.provinceRequired),
      city: yup.string().required(enumStrings.validationMessages.cityRequired),
      postalCode: yup
        .string()
        .required(enumStrings.validationMessages.postalCodeRequired),
      streetAddress: yup
        .string()
        .required(enumStrings.validationMessages.streetAddressRequired),
    }),
    supportContactEmail: yup
      .string()
      .required(enumStrings.validationMessages.supportEmailRequired),
    supportContactName: yup.string().required(enumStrings.ChamberAdminManage.sections.adminContactNameRequired),
    billingContactName: yup.string().required(enumStrings.ChamberAdminManage.sections.billingContactNameRequired),
    billingContactEmail: yup.string().email(enumStrings.ChamberAdminManage.sections.validEmailRequired).required(enumStrings.ChamberAdminManage.sections.billingContactEmailRequired),
    signingAuthorityContactName: yup.string().required(enumStrings.ChamberAdminManage.sections.signingAuthorityContactNameRequired),
    signingAuthorityContactEmail: yup.string().email(enumStrings.ChamberAdminManage.sections.validEmailRequired).required(enumStrings.ChamberAdminManage.sections.signingAuthorityContactEmailRequired),
    supportLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    jobPostingLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    eventsLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    newsletterLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    surveyLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    advocacyWinsLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    callForNominationsLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    memberSignupLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    premiumChamberMarketerLinkData: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
    sponsorLogoLink: yup
      .string()
      .matches(validWebsiteRegEx, enumStrings.validationMessages.validWebsite),
  });

  const intitalValuesMemo = useMemo(() => {
    const initialValues: ChamberInput = {
      id: chamberInfo?.id || "",
      name: chamberInfo?.name || "",
      description: chamberInfo?.description || "",
      website: chamberInfo?.website || "",
      supportContactEmail: chamberInfo?.supportContactEmail || "",
      supportContactName: chamberInfo?.supportContactName || "",
      billingContactName: (chamberInfo as any)?.billingContactName || "",
      billingContactEmail: (chamberInfo as any)?.billingContactEmail || "",
      signingAuthorityContactName: (chamberInfo as any)?.signingAuthorityContactName || "",
      signingAuthorityContactEmail: (chamberInfo as any)?.signingAuthorityContactEmail || "",
      supportLink: chamberInfo?.supportLink || "",
      phoneNumber: chamberInfo?.phoneNumber || "",
      phoneExtension: chamberInfo?.phoneExtension || "",
      facebook: chamberInfo?.facebook || "",
      instagram: chamberInfo?.instagram || "",
      threads: chamberInfo?.threads || "",
      linkedin: chamberInfo?.linkedin || "",
      twitter: chamberInfo?.twitter || "",
      tiktok: chamberInfo?.tiktok || "",
      youtube: chamberInfo?.youtube || "",
      jobPostingLink: chamberInfo?.jobPostingLink || "",
      address: {
        city: chamberInfo?.address.city || "",
        postalCode: chamberInfo?.address.postalCode || "",
        province: chamberInfo?.address.province || "",
        streetAddress: chamberInfo?.address.streetAddress || "",
      },
      eventsLink: chamberInfo?.eventsLink || "",
      newsletterLink: chamberInfo?.newsletterLink || "",
      surveyLink: chamberInfo?.surveyLink || "",
      advocacyWinsLink: chamberInfo?.advocacyWinsLink || "",
      callForNominationsLink: chamberInfo?.callForNominationsLink || "",
      memberSignupLink: chamberInfo?.memberSignupLink || "",
      logo: chamberInfo?.logo || undefined,
      sponsorLogo: chamberInfo?.sponsorLogo || undefined,
      sponsorLogoLink: chamberInfo?.sponsorLogoLink || undefined,
      ...(userType === UserType.RootAdmin
        ? {
          premiumChamberMarketerLink:
            premiumChamberMarketerLinkData?.getChamber
              ?.premiumChamberMarketerLink || undefined,
          hideInApp: chamberInfo?.hideInApp || false,
          subscriptionPlanType:
            subscriptionPlanTypeData?.getChamber?.subscriptionPlanType ||
            ChamberPerksAppSubscriptionPlanTypesEnum.Standard,
        }
        : {}),
      directoryBanner: chamberInfo?.directoryBanner || undefined,
      profileBanner: chamberInfo?.profileBanner || undefined,
    };
    return omitDeep(initialValues, "__typename");
  }, [
    chamberInfo,
    premiumChamberMarketerLinkData?.getChamber?.premiumChamberMarketerLink,
    subscriptionPlanTypeData?.getChamber?.subscriptionPlanType,
    userType,
  ]) as unknown as ChamberInput;

  const formik = useFormik({
    initialValues: intitalValuesMemo,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (input: ChamberInput) => {
      setLoader(true);
      try {
        setButtonDisabled(true);
        await updateChamber({
          variables: {
            id: input.id,
            input: {
              address: {
                city: input.address.city,
                postalCode: input.address.postalCode,
                province: input.address.province,
                streetAddress: input.address.streetAddress,
              },
              eventsLink: input.eventsLink,
              jobPostingLink: input.jobPostingLink,
              newsletterLink: input.newsletterLink,
              surveyLink: input.surveyLink,
              advocacyWinsLink: input.advocacyWinsLink,
              callForNominationsLink: input.callForNominationsLink,
              memberSignupLink: input.memberSignupLink,
              name: input.name,
              description: input.description,
              supportContactEmail: input.supportContactEmail
                .trim()
                .toLowerCase(),
              supportContactName: input.supportContactName,
              billingContactName: (input as any).billingContactName,
              billingContactEmail: ((input as any).billingContactEmail || "").trim().toLowerCase(),
              signingAuthorityContactName: (input as any).signingAuthorityContactName,
              signingAuthorityContactEmail: ((input as any).signingAuthorityContactEmail || "").trim().toLowerCase(),
              supportLink: input.supportLink,
              phoneNumber: input.phoneNumber,
              facebook: input.facebook,
              instagram: input.instagram,
              threads: input.threads,
              linkedin: input.linkedin,
              twitter: input.twitter,
              tiktok: input.tiktok,
              youtube: input.youtube,
              website: input.website,
              logo: input.logo,
              sponsorLogo: input.sponsorLogo,
              sponsorLogoLink: input.sponsorLogoLink,
              id: input.id,
              ...(userType === UserType.RootAdmin
                ? {
                  premiumChamberMarketerLink:
                    input.premiumChamberMarketerLink,
                  hideInApp: input.hideInApp,
                  subscriptionPlanType: input.subscriptionPlanType,
                }
                : {}),
              directoryBanner: input.directoryBanner,
              profileBanner: input.profileBanner,
            },
          },
          refetchQueries: [
            "getUser",
            "getUser_RootAdminChamberAdmin",
            "getChamber",
            "getAllChambers",
          ],
        });
        setLoader(false);
        setButtonDisabled(false);

        setUpdateSuccessOpen(true);
        setTimeout(() => {
          setUpdateSuccessOpen(false);
        }, 2000);
        // Clear the dirty flag so the UnsavedChangesPrompt doesn't trigger after a successful save.
        formik.resetForm({ values: formik.values });
      } catch (error) {
        setInvalidError(true);
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  // Scroll to first validation error on submit
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const firstErrorField = document.querySelector('.Mui-error, .MuiFormHelperText-root.Mui-error');
      if (firstErrorField) {
        const container = firstErrorField.closest('.MuiFormControl-root') || firstErrorField;
        container.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors]);

  const handleCreateBase64 = useCallback(
    async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      const base64 = await convertToBase64(file);
      formik.setFieldValue("logo", base64);
    },
    [formik]
  );

  const deleteLogo = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setDeleteLogoConfirmationOpen(true);
  };

  const confirmDeleteLogo = () => {
    formik.setFieldValue("logo", undefined);
    setDeleteLogoConfirmationOpen(false);
  };

  const cancelDeleteLogo = () => {
    setDeleteLogoConfirmationOpen(false);
  };

  const handleCreateBase64SponsorLogo = useCallback(
    async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      const base64 = await convertToBase64(file);
      formik.setFieldValue("sponsorLogo", base64);
    },
    [formik]
  );

  const deleteSponsorLogo = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setDeleteSponsorLogoConfirmationOpen(true);
  };

  const confirmDeleteSponsorLogo = () => {
    formik.setFieldValue("sponsorLogo", undefined);
    formik.setFieldValue("sponsorLogoLink", undefined);
    setDeleteSponsorLogoConfirmationOpen(false);
  };

  const cancelDeleteSponsorLogo = () => {
    setDeleteSponsorLogoConfirmationOpen(false);
  };

  const [
    chamberChangeAdminConsoleLoginEmailModalIsOpen,
    setChamberChangeAdminConsoleLoginEmailModalIsOpen,
  ] = useState(false);

  const [showPlanLimitationModal, setShowPlanLimitationModal] = useState(false);
  const [deleteLogoConfirmationOpen, setDeleteLogoConfirmationOpen] = useState(false);
  const [deleteSponsorLogoConfirmationOpen, setDeleteSponsorLogoConfirmationOpen] = useState(false);

  // Profile Banner Modal State
  const [profileBannerModalOpen, setProfileBannerModalOpen] = useState(false);
  const [currentProfileBannerIndex, setCurrentProfileBannerIndex] = useState(0);
  const [profileBannersData, setProfileBannersData] = useState<
    Array<{
      image: string | null;
      videoUrl: string | null;
      redirectUrl: string | null;
    }>
  >([
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
  ]);

  // Directory Banner Modal State
  const [directoryBannerModalOpen, setDirectoryBannerModalOpen] = useState(false);
  const [currentDirectoryBannerIndex, setCurrentDirectoryBannerIndex] = useState(0);
  const [directoryBannersData, setDirectoryBannersData] = useState<
    Array<{
      image: string | null;
      videoUrl: string | null;
      redirectUrl: string | null;
    }>
  >([
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
  ]);

  // Delete Banner Confirmation State
  const [deleteBannerModalOpen, setDeleteBannerModalOpen] = useState(false);
  const [bannerDeletedSuccessOpen, setBannerDeletedSuccessOpen] = useState(false);
  const [pendingDeleteBanner, setPendingDeleteBanner] = useState<{
    type: "profile" | "directory";
    index: number;
  } | null>(null);

  // Preview Modal State
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewBannersList, setPreviewBannersList] = useState<
    Array<{
      image: string | null;
      videoUrl: string | null;
      redirectUrl: string | null;
    }>
  >([]);

  const handlePreviewProfileBanners = () => {
    setPreviewBannersList(profileBannersData);
    setPreviewModalOpen(true);
  };

  const handlePreviewDirectoryBanners = () => {
    setPreviewBannersList(directoryBannersData);
    setPreviewModalOpen(true);
  };

  // Sync banner data from chamberInfo when it loads
  useEffect(() => {
    if (chamberInfo?.profileBanner) {
      const initialProfileBanners = [
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
      ];
      chamberInfo.profileBanner.forEach((banner: any, index: number) => {
        if (index < 4) {
          const isVideo = banner.contentType === "VIDEO";
          initialProfileBanners[index] = {
            image: !isVideo ? (banner.contentUrl || null) : null,
            videoUrl: isVideo ? (banner.contentUrl || null) : null,
            redirectUrl: banner.imageLink || null,
          };
        }
      });
      setProfileBannersData(initialProfileBanners);
    }
    if (chamberInfo?.directoryBanner) {
      const initialDirectoryBanners = [
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
      ];
      chamberInfo.directoryBanner.forEach((banner: any, index: number) => {
        if (index < 4) {
          const isVideo = banner.contentType === "VIDEO";
          initialDirectoryBanners[index] = {
            image: !isVideo ? (banner.contentUrl || null) : null,
            videoUrl: isVideo ? (banner.contentUrl || null) : null,
            redirectUrl: banner.imageLink || null,
          };
        }
      });
      setDirectoryBannersData(initialDirectoryBanners);
    }
  }, [chamberInfo?.profileBanner, chamberInfo?.directoryBanner]);

  // Profile Banner Handlers
  const handleOpenProfileBannerModal = (index: number) => {
    setCurrentProfileBannerIndex(index);
    setProfileBannerModalOpen(true);
  };

  const convertBannersToFormikFormat = (bannersData: Array<{ image: string | null; videoUrl: string | null; redirectUrl: string | null }>) => {
    return bannersData
      .filter(b => b.image || b.videoUrl)
      .map((b, index) => ({
        contentUrl: b.image || b.videoUrl || "",
        imageLink: b.redirectUrl || "",
        contentType: b.videoUrl ? "VIDEO" : "IMAGE",
        position: index,
      }));
  };

  const handleSaveProfileBanner = (data: {
    image: string | null;
    videoUrl: string | null;
    redirectUrl: string | null;
  }) => {
    const newBannersData = [...profileBannersData];
    newBannersData[currentProfileBannerIndex - 1] = data;
    setProfileBannersData(newBannersData);
    setProfileBannerModalOpen(false);
    formik.setFieldValue("profileBanner", convertBannersToFormikFormat(newBannersData));
  };

  const handleMoveProfileBanner = (index: number, direction: 'left' | 'right') => {
    const newBannersData = [...profileBannersData];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newBannersData.length) {
      const temp = newBannersData[index];
      newBannersData[index] = newBannersData[targetIndex];
      newBannersData[targetIndex] = temp;
      setProfileBannersData(newBannersData);
      formik.setFieldValue("profileBanner", convertBannersToFormikFormat(newBannersData));
    }
  };

  const handleDeleteProfileBanner = (index: number) => {
    setPendingDeleteBanner({ type: "profile", index });
    setDeleteBannerModalOpen(true);
  };

  // Directory Banner Handlers
  const handleOpenDirectoryBannerModal = (index: number) => {
    setCurrentDirectoryBannerIndex(index);
    setDirectoryBannerModalOpen(true);
  };

  const handleSaveDirectoryBanner = (data: {
    image: string | null;
    videoUrl: string | null;
    redirectUrl: string | null;
  }) => {
    const newBannersData = [...directoryBannersData];
    newBannersData[currentDirectoryBannerIndex - 1] = data;
    setDirectoryBannersData(newBannersData);
    setDirectoryBannerModalOpen(false);
    formik.setFieldValue("directoryBanner", convertBannersToFormikFormat(newBannersData));
  };

  const handleMoveDirectoryBanner = (index: number, direction: 'left' | 'right') => {
    const newBannersData = [...directoryBannersData];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newBannersData.length) {
      const temp = newBannersData[index];
      newBannersData[index] = newBannersData[targetIndex];
      newBannersData[targetIndex] = temp;
      setDirectoryBannersData(newBannersData);
      formik.setFieldValue("directoryBanner", convertBannersToFormikFormat(newBannersData));
    }
  };

  const handleDeleteDirectoryBanner = (index: number) => {
    setPendingDeleteBanner({ type: "directory", index });
    setDeleteBannerModalOpen(true);
  };

  const confirmDeleteBanner = () => {
    if (!pendingDeleteBanner) return;
    const { type, index } = pendingDeleteBanner;
    if (type === "profile") {
      const newBannersData = [...profileBannersData];
      newBannersData[index] = { image: null, videoUrl: null, redirectUrl: null };
      setProfileBannersData(newBannersData);
      formik.setFieldValue("profileBanner", convertBannersToFormikFormat(newBannersData));
    } else {
      const newBannersData = [...directoryBannersData];
      newBannersData[index] = { image: null, videoUrl: null, redirectUrl: null };
      setDirectoryBannersData(newBannersData);
      formik.setFieldValue("directoryBanner", convertBannersToFormikFormat(newBannersData));
    }
    setPendingDeleteBanner(null);
    setBannerDeletedSuccessOpen(true);
  };

  const openChamberChangeAdminConsoleLoginEmailModal = useCallback(() => {
    setChamberChangeAdminConsoleLoginEmailModalIsOpen(true);
  }, []);

  const closeChamberChangeAdminConsoleLoginEmailModal = useCallback(() => {
    setChamberChangeAdminConsoleLoginEmailModalIsOpen(false);
  }, []);

  const handlePlanLimitationModalOpen = useCallback(() => {
    setShowPlanLimitationModal(true);
  }, []);

  const handlePlanLimitationModalClose = useCallback(() => {
    setShowPlanLimitationModal(false);
  }, []);

  const rootAdminObj =
    userType === UserType.ChamberAdmin
      ? enumStrings.groups[UserType.ChamberAdmin][
      RedirectUri.manageYourChamberChamberAdmin
      ]
      : enumStrings.groups[UserType.RootAdmin][RedirectUri.updateAChamberRoute];

  return (
    <div style={{
      backgroundColor: theme.palette.primary.main,
      minHeight: "100vh",
      paddingTop: "10px",
      paddingBottom: "80px",
      overflowX: "hidden",
    }}>
      <UnsavedChangesPrompt isDirty={formik.dirty} />
      <div className={classes.titleContent}>
        <div className={classes.titleRow}>
          <div
            className={classes.title}
            style={{
              flexDirection: "column",
            }}
          >
            <span>{rootAdminObj.title}</span>
            <span
              style={{
                marginLeft: "1px",
                fontWeight: "normal",
                fontSize: isMobile ? "16px" : "18px",
                lineHeight: isMobile ? "1.55" : undefined,
              }}
            >
              {rootAdminObj.description}
            </span>
            <div style={{ height: 6 }} />
            <div
              className={classes.pageHeaderButtonRow}
              style={{ display: "flex", gap: isMobile ? "8px" : "12px", alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}
            >
              <VideoTutorialButton
                tutorialUrl="https://share.synthesia.io/embeds/videos/e9ae72e1-71f3-44fb-a5bc-d71d35cab428"
              />
              <ExportButton
                customOnClick={() => openInstructionPdf("Manage Chamber Profile.pdf")}
                icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
                label={enumStrings.manageChamberEngage.viewInstructions}
                disablePadding
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.content}>
        <div>
          <form
            className={classes.formContainer}
            onSubmit={formik.handleSubmit}
            autoComplete="off"
          >
            {open && (
              <ConfirmationModal
                open={open}
                loading={loading}
                handleClose={handleClose}
                message={enumStrings.perkPartnerAdmin.deleteConfirmation}
                buttonConfig={buttonConfig}
              />
            )}
            {chamberInfo ? (
              <>
                <>
                  {userType === UserType.RootAdmin ? (
                    <>
                      <ChamberSubscriptionPlanRadioGroup
                        formikSetFieldValue={formik.setFieldValue}
                        formikValues={formik.values}
                        formikErrors={formik.errors}
                      />
                      <TextField
                        type="text"
                        label={enumStrings.createChamberText.emailUsername}
                        disabled
                        className={classes.companyInput}
                        variant="outlined"
                        inputProps={{ autoComplete: "new-password" }}
                        id="adminConsoleLoginEmail"
                        name="adminConsoleLoginEmail"
                        value={
                          adminConsoleLoginEmailForChamber.data
                            ?.getAdminConsoleLoginEmailForChamber
                            .adminConsoleLoginEmail || ""
                        }
                      />
                      <div
                        style={{
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                          textDecoration: "underline",
                          padding: "5px",
                          paddingBottom: 0,
                          marginBottom: 0,
                          cursor: "pointer",
                        }}
                        onClick={openChamberChangeAdminConsoleLoginEmailModal}
                      >
                        {enumStrings.changeEmailUsername}
                      </div>
                    </>
                  ) : null}

                  {/* Chamber Information Section (internal contacts) */}
                  <div className={classes.formSection}>
                    <div className={classes.sectionHeader}>
                      <p className={classes.sectionTitle}>
                        {enumStrings.ChamberAdminManage.sections.chamberInformationTitle}
                      </p>
                    </div>
                    <p className={classes.sectionHelperText}>
                      {enumStrings.ChamberAdminManage.sections.chamberInformationDescPart1}
                      <strong>{enumStrings.ChamberAdminManage.sections.chamberInformationDescPart2}</strong>
                    </p>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Chamber Perks App™ Administrator Contact<span className={classes.requiredAsterisk}>*</span></span>
                        </div>
                        <p style={{ fontSize: "12px", color: "#666666", margin: "0 0 8px 0" }}>{enumStrings.ChamberAdminManage.sections.adminContactNameHelper}</p>
                        <TextField
                          type="text"
                          placeholder={enumStrings.ChamberAdminManage.sections.adminContactNamePlaceholder}
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="supportContactName"
                          name="supportContactName"
                          value={formik.values.supportContactName}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.supportContactName &&
                            Boolean(formik.errors.supportContactName)
                          }
                          helperText={
                            formik.touched.supportContactName &&
                            formik.errors.supportContactName
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Chamber Perks App™ Administrator Email<span className={classes.requiredAsterisk}>*</span></span>
                        </div>
                        <p style={{ fontSize: "12px", color: "#666666", margin: "0 0 8px 0" }}>{enumStrings.ChamberAdminManage.sections.adminContactEmailHelper}</p>
                        <TextField
                          type="text"
                          placeholder={enumStrings.ChamberAdminManage.sections.adminContactEmailPlaceholder}
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="supportContactEmail"
                          name="supportContactEmail"
                          value={formik.values.supportContactEmail}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.supportContactEmail &&
                            Boolean(formik.errors.supportContactEmail)
                          }
                          helperText={
                            formik.touched.supportContactEmail &&
                            formik.errors.supportContactEmail
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>{enumStrings.ChamberAdminManage.sections.billingContactName}<span className={classes.requiredAsterisk}>*</span></span>
                        </div>
                        <TextField
                          type="text"
                          placeholder={enumStrings.ChamberAdminManage.sections.billingContactNamePlaceholder}
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="billingContactName"
                          name="billingContactName"
                          value={(formik.values as any).billingContactName}
                          onChange={formik.handleChange}
                          error={
                            (formik.touched as any).billingContactName &&
                            Boolean((formik.errors as any).billingContactName)
                          }
                          helperText={
                            (formik.touched as any).billingContactName &&
                            (formik.errors as any).billingContactName
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>{enumStrings.ChamberAdminManage.sections.billingContactEmail}<span className={classes.requiredAsterisk}>*</span></span>
                        </div>
                        <TextField
                          type="text"
                          placeholder={enumStrings.ChamberAdminManage.sections.billingContactEmailPlaceholder}
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="billingContactEmail"
                          name="billingContactEmail"
                          value={(formik.values as any).billingContactEmail}
                          onChange={formik.handleChange}
                          error={
                            (formik.touched as any).billingContactEmail &&
                            Boolean((formik.errors as any).billingContactEmail)
                          }
                          helperText={
                            (formik.touched as any).billingContactEmail &&
                            (formik.errors as any).billingContactEmail
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>{enumStrings.ChamberAdminManage.sections.signingAuthorityContactName}<span className={classes.requiredAsterisk}>*</span></span>
                        </div>
                        <TextField
                          type="text"
                          placeholder={enumStrings.ChamberAdminManage.sections.signingAuthorityContactNamePlaceholder}
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="signingAuthorityContactName"
                          name="signingAuthorityContactName"
                          value={(formik.values as any).signingAuthorityContactName}
                          onChange={formik.handleChange}
                          error={
                            (formik.touched as any).signingAuthorityContactName &&
                            Boolean((formik.errors as any).signingAuthorityContactName)
                          }
                          helperText={
                            (formik.touched as any).signingAuthorityContactName &&
                            (formik.errors as any).signingAuthorityContactName
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>{enumStrings.ChamberAdminManage.sections.signingAuthorityContactEmail}<span className={classes.requiredAsterisk}>*</span></span>
                        </div>
                        <TextField
                          type="text"
                          placeholder={enumStrings.ChamberAdminManage.sections.signingAuthorityContactEmailPlaceholder}
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="signingAuthorityContactEmail"
                          name="signingAuthorityContactEmail"
                          value={(formik.values as any).signingAuthorityContactEmail}
                          onChange={formik.handleChange}
                          error={
                            (formik.touched as any).signingAuthorityContactEmail &&
                            Boolean((formik.errors as any).signingAuthorityContactEmail)
                          }
                          helperText={
                            (formik.touched as any).signingAuthorityContactEmail &&
                            (formik.errors as any).signingAuthorityContactEmail
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Chamber Profile on Chamber Perks App™ Section */}
                  <div className={classes.formSection}>
                    <div className={classes.sectionHeader}>
                      <p className={classes.sectionTitle}>
                        {enumStrings.ChamberAdminManage.sections.chamberProfileOnCpa}
                      </p>
                    </div>
                    <p className={classes.sectionHelperText}>
                      {enumStrings.ChamberAdminManage.sections.chamberProfileOnCpaDesc}
                    </p>

                    {/* Row 1-2: Chamber/Board Name + Website (left) | Chamber/Board Description (right, spans 2 rows) */}
                    <div style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? "20px" : "24px",
                      marginBottom: "20px",
                    }}>
                      {/* Left Column: Name + Website stacked (on mobile, lifts children into outer flex so order can interleave with Description) */}
                      <div style={{ flex: 1, display: isMobile ? "contents" : "flex", flexDirection: "column", gap: "20px" }}>
                        {/* Chamber/Board Name */}
                        <div style={isMobile ? { order: 1 } : undefined}>
                          <div className={classes.fieldLabel}>
                            <span>Chamber Name<span className={classes.requiredAsterisk}>*</span></span>
                          </div>
                          <TextField
                            type="text"
                            placeholder="Enter Chamber Name"
                            className={classes.sectionInput}
                            variant="outlined"
                            inputProps={{ autoComplete: "new-password" }}
                            id="name"
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            helperText={formik.touched.name && formik.errors.name}
                            InputLabelProps={{ shrink: false }}
                          />
                        </div>

                        {/* Chamber/Board Website */}
                        <div style={isMobile ? { order: 3 } : undefined}>
                          <div className={classes.fieldLabel}>
                            <span>Chamber Website<span className={classes.requiredAsterisk}>*</span></span>
                          </div>
                          <TextField
                            type="text"
                            placeholder="Enter Chamber Website URL"
                            className={classes.sectionInput}
                            variant="outlined"
                            inputProps={{ autoComplete: "new-password" }}
                            id="website"
                            name="website"
                            value={formik.values.website}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.website && Boolean(formik.errors.website)
                            }
                            helperText={
                              formik.touched.website && formik.errors.website
                            }
                            InputLabelProps={{ shrink: false }}
                          />
                        </div>
                      </div>

                      {/* Right Column: Description (matches height of left column) */}
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", ...(isMobile ? { order: 2 } : {}) }}>
                        <div className={classes.fieldLabel}>
                          <span>{enumStrings.ChamberAdminManage.chamberDescriptionLabel}</span>
                        </div>
                        <div style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "24px",
                          color: "#454B58",
                          marginBottom: "10px",
                        }}>
                          {enumStrings.ChamberAdminManage.chamberDescriptionSubLabel}
                        </div>
                        <div style={{
                          border: descriptionGeneratedByIda
                            ? `2px solid ${theme.palette.success.main}`
                            : `1px solid ${theme.palette.text.primary}`,
                          borderRadius: "8px",
                          backgroundColor: "#FFFFFF",
                          padding: "16px",
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}>
                          {descriptionGeneratedByIda && (
                            <div style={{
                              fontFamily: "DM Sans, sans-serif",
                              fontWeight: 700,
                              fontSize: "10px",
                              lineHeight: "15px",
                              color: theme.palette.success.main,
                              marginBottom: "4px",
                            }}>
                              {enumStrings.idaGenerate.chamberGeneratedLabel}
                            </div>
                          )}
                          <TextField
                            type="text"
                            placeholder="Enter Details About Your Chamber"
                            variant="standard"
                            multiline
                            rows={4}
                            inputProps={{
                              maxLength: CHARACTER_LIMIT_DESCRIPTION,
                            }}
                            id="description"
                            name="description"
                            value={formik.values.description}
                            onChange={(e) => {
                              formik.handleChange(e);
                              if (descriptionGeneratedByIda) {
                                setDescriptionGeneratedByIda(false);
                              }
                            }}
                            error={
                              formik.touched.description &&
                              Boolean(formik.errors.description)
                            }
                            InputProps={{
                              disableUnderline: true,
                            }}
                            InputLabelProps={{ shrink: false }}
                            style={{ flex: 1 }}
                            fullWidth
                          />
                        </div>
                        {/* IDA AI Helper Section - below the textarea: counter left, icon + generate right */}
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "12px", marginTop: "12px", flexWrap: "wrap" }}>
                          <div style={{ fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "24px", color: (formik.values?.description?.length || 0) > CHARACTER_LIMIT_DESCRIPTION ? theme.palette.delete.main : "#454B58", textAlign: "left" }}>
                            {`${formik.values?.description?.length || 0}/${CHARACTER_LIMIT_DESCRIPTION} characters`}
                          </div>
                          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px" }}>
                            <img src={IdaChatbotIcon} alt="IDA" style={{ width: "40px", height: "40px", objectFit: "contain" }} />
                            {chamberInfo?.isSandbox ? (
                              <Button
                                variant="contained"
                                onClick={() => {
                                  setIdaKeyDetails(formik.values.description || "");
                                  setIdaDialogOpen(true);
                                }}
                                style={{
                                  backgroundColor: "#4CAF50",
                                  color: "#FFFFFF",
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  borderRadius: "8px",
                                  padding: "8px 24px",
                                  textTransform: "uppercase",
                                  minHeight: "36px",
                                  boxShadow: "none",
                                }}
                              >
                                {descriptionGeneratedByIda ? enumStrings.idaGenerate.idaRegenerate : enumStrings.idaGenerate.generateButton}
                              </Button>
                            ) : (
                              <span style={{
                                backgroundColor: "#E53935",
                                color: "#FFFFFF",
                                border: "1px solid #E53935",
                                borderRadius: "10px",
                                padding: "6px 14px",
                                fontSize: "12px",
                                fontWeight: 700,
                                whiteSpace: "nowrap" as const,
                                display: "inline-block",
                              }}>
                                COMING SOON
                              </span>
                            )}
                          </div>
                        </div>
                        {formik.touched.description && formik.errors.description && (
                          <div style={{ fontSize: "13px", color: theme.palette.delete.main, textAlign: "left", marginTop: "2px" }}>
                            {formik.errors.description}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 3: Chamber Contact Phone | Google Review URL */}
                    <div className={classes.formRow}>
                      <div>
                        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                          <div style={{ flexGrow: 1 }}>
                            <div className={classes.fieldLabel} style={{ marginBottom: "12px" }}>
                              <span>Chamber Contact Phone<span className={classes.requiredAsterisk}>*</span></span>
                            </div>
                            <PhoneNumberFormat
                              formikErrors={formik.errors.phoneNumber}
                              formikVal={formik.values.phoneNumber}
                              handleChange={formik.handleChange}
                              label=""
                              placeholder="Enter Chamber Contact Phone"
                              error={
                                formik.touched.phoneNumber &&
                                Boolean(formik.errors.phoneNumber)
                              }
                              helperText={
                                formik.touched.phoneNumber && formik.errors.phoneNumber
                              }
                              className={classes.sectionInput}
                              style={{ marginTop: 0 }}
                            />
                          </div>
                          <div style={{ flexShrink: 0, width: "100px" }}>
                            <div className={classes.fieldLabel} style={{ marginBottom: "12px" }}>
                              <span>{enumStrings.ChamberAdminManage?.extensionNo || "Ext."}</span>
                            </div>
                            <TextField
                              type="text"
                              placeholder={enumStrings.ChamberAdminManage?.extensionPlaceholder || "00000"}
                              className={classes.sectionInput}
                              variant="outlined"
                              inputProps={{ autoComplete: "new-password" }}
                              id="phoneExtension"
                              name="phoneExtension"
                              value={formik.values.phoneExtension || ""}
                              onChange={formik.handleChange}
                              InputLabelProps={{ shrink: false }}
                              style={{ marginTop: 0 }}
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Google Review URL <span className={classes.optionalText}>(optional)</span></span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Google Review URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="supportLink"
                          name="supportLink"
                          value={formik.values.supportLink}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.supportLink &&
                            Boolean(formik.errors.supportLink)
                          }
                          helperText={
                            formik.touched.supportLink && formik.errors.supportLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    {/* Row 5: Support Contact Email | Chamber Support Page URL */}
                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Chamber Support Email <span className={classes.optionalText}>(optional)</span></span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Chamber Support Email"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="secondarySupportEmail"
                          name="secondarySupportEmail"
                          value={formik.values.secondarySupportEmail || ""}
                          onChange={formik.handleChange}
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Chamber Support Page URL <span className={classes.optionalText}>(optional)</span></span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Chamber Support Page URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="chamberSupportPageUrl"
                          name="chamberSupportPageUrl"
                          value={formik.values.chamberSupportPageUrl || ""}
                          onChange={formik.handleChange}
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    {/* Row 6: Chamber Logo */}
                    <div className={classes.formRowSingle}>
                      <div>
                        <div className={classes.fieldLabel} style={{ justifyContent: "flex-start" }}>
                          <span style={{ fontWeight: 700 }}>Chamber Logo</span>
                        </div>
                        <div style={{ fontSize: "14px", color: "#000000", marginBottom: "10px" }}>
                          Logo will be shown in the Chamber Perks App™
                        </div>
                        <LogoPicker
                          logo={formik.values.logo}
                          deleteLogo={deleteLogo}
                          base64={(e: any) => handleCreateBase64(e)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company Address Section */}
                  <div className={classes.formSection}>
                    <div className={classes.sectionHeader}>
                      <p className={classes.sectionTitle}>
                        Chamber Address
                      </p>
                    </div>
                    <p className={classes.sectionHelperText}>
                      Enter the information you want to appear in your chamber profile and map on the Chamber Perks App™. Leave any <strong>fields blank</strong> that you don't want to appear in the app.
                    </p>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Street Address or PO Box</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Street Number and Name"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="address.streetAddress"
                          name="address.streetAddress"
                          value={formik.values.address?.streetAddress}
                          onChange={formik.handleChange}
                          error={
                            (formik.touched.address as any)?.streetAddress &&
                            Boolean((formik.errors.address as any)?.streetAddress)
                          }
                          helperText={
                            (formik.touched.address as any)?.streetAddress &&
                            (formik.errors.address as any)?.streetAddress
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>City</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter City Name"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="address.city"
                          name="address.city"
                          value={formik.values.address?.city}
                          onChange={formik.handleChange}
                          error={
                            (formik.touched.address as any)?.city &&
                            Boolean((formik.errors.address as any)?.city)
                          }
                          helperText={
                            (formik.touched.address as any)?.city &&
                            (formik.errors.address as any)?.city
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Province/State</span>
                        </div>
                        <ProvincePicker
                          formikVal={formik.values.address?.province}
                          formikErrors={
                            (formik.touched.address as any)?.province &&
                            Boolean((formik.errors.address as any)?.province)
                          }
                          formikStringError={(formik.errors.address as any)?.province}
                          formikSetFieldValue={formik.setFieldValue}
                          inline
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Postal/Zip Code</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Postal/Zip Code"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="address.postalCode"
                          name="address.postalCode"
                          value={formik.values.address?.postalCode}
                          onChange={formik.handleChange}
                          error={
                            (formik.touched.address as any)?.postalCode &&
                            Boolean((formik.errors.address as any)?.postalCode)
                          }
                          helperText={
                            (formik.touched.address as any)?.postalCode &&
                            (formik.errors.address as any)?.postalCode
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Links to Socials Section */}
                  <div className={classes.formSection}>
                    <div className={classes.sectionHeader}>
                      <p className={classes.sectionTitle}>
                        Links to Socials
                      </p>
                    </div>
                    <p className={classes.sectionHelperText}>
                      Enter and update your chamber links to socials which will appear on your chamber profile on the Chamber Perks App™.
                    </p>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Facebook Business Page URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Facebook Business Page URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="facebook"
                          name="facebook"
                          value={formik.values.facebook}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.facebook &&
                            Boolean(formik.errors.facebook)
                          }
                          helperText={
                            formik.touched.facebook && formik.errors.facebook
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Instagram Account URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Instagram Account URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="instagram"
                          name="instagram"
                          value={formik.values.instagram}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.instagram &&
                            Boolean(formik.errors.instagram)
                          }
                          helperText={
                            formik.touched.instagram && formik.errors.instagram
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Threads Account URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Threads Account URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="threads"
                          name="threads"
                          value={formik.values.threads}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.threads && Boolean(formik.errors.threads)
                          }
                          helperText={
                            formik.touched.threads && formik.errors.threads
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>LinkedIn Page URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter LinkedIn Page URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="linkedin"
                          name="linkedin"
                          value={formik.values.linkedin}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.linkedin &&
                            Boolean(formik.errors.linkedin)
                          }
                          helperText={
                            formik.touched.linkedin && formik.errors.linkedin
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>X (Twitter) Account URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter X (Twitter) Account URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="twitter"
                          name="twitter"
                          value={formik.values.twitter}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.twitter && Boolean(formik.errors.twitter)
                          }
                          helperText={
                            formik.touched.twitter && formik.errors.twitter
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>TikTok Account URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter TikTok Account URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="tiktok"
                          name="tiktok"
                          value={formik.values.tiktok}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.tiktok && Boolean(formik.errors.tiktok)
                          }
                          helperText={formik.touched.tiktok && formik.errors.tiktok}
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Youtube Channel URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Youtube Channel URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="youtube"
                          name="youtube"
                          value={formik.values.youtube}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.youtube && Boolean(formik.errors.youtube)
                          }
                          helperText={
                            formik.touched.youtube && formik.errors.youtube
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div />
                    </div>
                  </div>

                  {/* My Chamber Tab Section */}
                  <div className={classes.formSection}>
                    <div className={classes.sectionHeader}>
                      <p className={classes.sectionTitle}>
                        My Chamber Tab
                      </p>
                    </div>
                    <p className={classes.sectionHelperText}>
                      {enumStrings.ChamberAdminManage.myChamberLinksDesc}
                    </p>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Events Link URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Events Link URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="eventsLink"
                          name="eventsLink"
                          value={formik.values.eventsLink}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.eventsLink &&
                            Boolean(formik.errors.eventsLink)
                          }
                          helperText={
                            formik.touched.eventsLink &&
                            formik.errors.eventsLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Newsletter Link URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Newsletter Link URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="newsletterLink"
                          name="newsletterLink"
                          value={formik.values.newsletterLink}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.newsletterLink &&
                            Boolean(formik.errors.newsletterLink)
                          }
                          helperText={
                            formik.touched.newsletterLink &&
                            formik.errors.newsletterLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Job Posting Link URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Job Posting Link URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="jobPostingLink"
                          name="jobPostingLink"
                          value={formik.values.jobPostingLink}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.jobPostingLink &&
                            Boolean(formik.errors.jobPostingLink)
                          }
                          helperText={
                            formik.touched.jobPostingLink &&
                            formik.errors.jobPostingLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Survey Link URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Survey Link URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="surveyLink"
                          name="surveyLink"
                          value={formik.values.surveyLink}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.surveyLink &&
                            Boolean(formik.errors.surveyLink)
                          }
                          helperText={
                            formik.touched.surveyLink &&
                            formik.errors.surveyLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Advocacy Wins Link URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Advocacy Wins Link URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="advocacyWinsLink"
                          name="advocacyWinsLink"
                          value={formik.values.advocacyWinsLink}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.advocacyWinsLink &&
                            Boolean(formik.errors.advocacyWinsLink)
                          }
                          helperText={
                            formik.touched.advocacyWinsLink &&
                            formik.errors.advocacyWinsLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>New Member Sign up Link URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter New Member Sign up Link URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="memberSignupLink"
                          name="memberSignupLink"
                          value={formik.values.memberSignupLink}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.memberSignupLink &&
                            Boolean(formik.errors.memberSignupLink)
                          }
                          helperText={
                            formik.touched.memberSignupLink &&
                            formik.errors.memberSignupLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Call for Nominations Link URL</span>
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Call for Nominations Link URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="callForNominationsLink"
                          name="callForNominationsLink"
                          value={formik.values.callForNominationsLink}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.callForNominationsLink &&
                            Boolean(formik.errors.callForNominationsLink)
                          }
                          helperText={
                            formik.touched.callForNominationsLink &&
                            formik.errors.callForNominationsLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                      <div />
                    </div>
                  </div>

                  {/* Chamber Perks App™ Sponsor Details Section */}
                  <div className={classes.formSection}>
                    <div className={classes.sectionHeader}>
                      <p className={classes.sectionTitle}>
                        Chamber Perks App™ Sponsor Details
                      </p>
                    </div>
                    <p className={classes.sectionHelperText}>
                      Enter the member company logo and website URL that will appear in the app under your chamber profile and on the main directory page. The sponsor's logo will also be displayed on every member's dashboard. Your chamber should activate a Featured Member upgrade to provide additional exposure through profile and perk banners. This sponsorship opportunity allows your chamber to generate non-dues revenue. Your chamber may set its own sponsorship fee.
                    </p>
                    <p className={classes.sectionHelperText} style={{ marginTop: 8 }}>
                      Chamber Member Pro™ Platform will also have the Sponsorship Logo appear on the Chamber Directory on their website
                    </p>

                    <div className={classes.formRow}>
                      <div>
                        <div className={classes.fieldLabel} style={{ justifyContent: "flex-start" }}>
                          <span style={{ fontWeight: 700 }}>Sponsor Logo</span>
                        </div>
                        <div style={{ fontSize: "14px", color: "#000000", marginBottom: "10px" }}>
                          Ideally a square PNG or JPG image 5MB in size
                        </div>
                        <LogoPicker
                          logo={formik.values.sponsorLogo}
                          deleteLogo={deleteSponsorLogo}
                          base64={(e: any) => handleCreateBase64SponsorLogo(e)}
                        />
                      </div>
                      <div>
                        <div className={classes.fieldLabel}>
                          <span>Sponsor Logo Link</span>
                        </div>
                        <div style={{ fontSize: "14px", color: "#000000", marginBottom: "8px" }}>
                          Enter Sponsor website link to your sponsor logo image
                        </div>
                        <TextField
                          type="text"
                          placeholder="Enter Website URL"
                          className={classes.sectionInput}
                          variant="outlined"
                          inputProps={{ autoComplete: "new-password" }}
                          id="sponsorLogoLink"
                          name="sponsorLogoLink"
                          value={formik.values.sponsorLogoLink || ""}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.sponsorLogoLink &&
                            Boolean(formik.errors.sponsorLogoLink)
                          }
                          helperText={
                            formik.touched.sponsorLogoLink &&
                            formik.errors.sponsorLogoLink
                          }
                          InputLabelProps={{ shrink: false }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Chamber Profile Banners Section */}
                  <div className={classes.formSection} style={isMobile ? undefined : { minHeight: "514.5px" }}>
                    <div className={classes.sectionHeader}>
                      <p className={classes.sectionTitle}>
                        Chamber Profile Banners
                      </p>
                    </div>
                    <p className={classes.sectionHelperText}>
                      Images and video banners uploaded here will appear on your chamber profile on the Chamber Perks App™. Use these banners to showcase your chamber, events, and initiatives.
                    </p>
                    <p className={classes.bannerHelperText}>
                      Upload up to 4 image or video banners to promote your chamber. An aspect ratio of 16:9 and a minimum size of 1280x720 are recommended for best quality.
                    </p>

                    <div className={classes.bannersGrid}>
                      {profileBannersData.map((banner, index) => (
                        <div key={index} style={{ display: "flex", width: isMobile ? "100%" : "209px", flexDirection: "column", alignItems: "center", gap: isMobile ? "8px" : "16px", minWidth: 0 }}>
                          <div style={{ textAlign: "center", fontWeight: 600, color: theme.palette.primary.main, fontSize: isMobile ? "15px" : "20px", lineHeight: "24px" }}>
                            Banner {index + 1}
                          </div>
                          {banner.image || banner.videoUrl ? (
                            <div className={classes.bannerCard}>
                              <div className={classes.bannerImageContainer}>
                                {banner.image ? (
                                  <img
                                    src={banner.image}
                                    alt={`Banner ${index + 1}`}
                                    className={classes.bannerImage}
                                  />
                                ) : (
                                  <div className={classes.videoPlaceholder}>
                                    <img src={PlayVideoIcon} alt="Play" style={{ width: "36px", height: "36px" }} />
                                    <span style={{ fontSize: "14px", fontWeight: 600, color: theme.palette.primary.main }}>Watch Video</span>
                                  </div>
                                )}
                              </div>
                              <div className={classes.bannerActions}>
                                <div className={classes.editDeleteButtons}>
                                  <IconButton
                                    size="small"
                                    className={classes.editButton}
                                    onClick={() => handleOpenProfileBannerModal(index + 1)}
                                  >
                                    <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px" }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    className={classes.trashButton}
                                    onClick={() => handleDeleteProfileBanner(index)}
                                  >
                                    <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "22px", filter: "brightness(0) invert(1)" }} />
                                  </IconButton>
                                </div>
                                <div className={classes.arrowButtons}>
                                  <IconButton
                                    size="small"
                                    className={classes.arrowButton}
                                    onClick={() => handleMoveProfileBanner(index, 'left')}
                                    disabled={index === 0}
                                  >
                                    <ArrowBackIosIcon style={{ fontSize: "12px", marginLeft: "4px" }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    className={classes.arrowButton}
                                    onClick={() => handleMoveProfileBanner(index, 'right')}
                                    disabled={index === profileBannersData.length - 1}
                                  >
                                    <ArrowForwardIosIcon style={{ fontSize: "12px" }} />
                                  </IconButton>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              onClick={() => handleOpenProfileBannerModal(index + 1)}
                              className={classes.bannerCardEmpty}
                            >
                              <img
                                src={UploadRoundIcon}
                                alt="Upload"
                                style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px" }}
                              />
                              <p style={{
                                color: theme.palette.primary.main,
                                fontSize: isMobile ? "12px" : "20px",
                                fontWeight: 600,
                                margin: 0,
                                lineHeight: "1.3",
                                textAlign: "center",
                              }}>
                                Upload Banner
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Footer Text */}
                    <p style={{
                      textAlign: "center",
                      fontSize: isMobile ? "14px" : "16px",
                      fontWeight: 400,
                      lineHeight: isMobile ? "20px" : "24px",
                      color: "#000000",
                      marginTop: "20px",
                      marginBottom: "15px",
                    }}>
                      After previewing your banners, click <span style={{ fontWeight: 700 }}>UPDATE</span> at bottom of page to upload them to the Chamber Perks App™.
                    </p>

                    {/* Preview Banners Button */}
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        className={classes.previewBannersButton}
                        onClick={handlePreviewProfileBanners}
                        disabled={!profileBannersData.some(b => b.image || b.videoUrl)}
                        style={profileBannersData.some(b => b.image || b.videoUrl) ? { backgroundColor: "#000000" } : {}}
                      >
                        {enumStrings.common.previewBanners}
                      </Button>
                    </div>
                  </div>

                  {/* Main Directory Banners Section */}
                  {subscriptionPlanTypeData?.getChamber?.subscriptionPlanType !== ChamberPerksAppSubscriptionPlanTypesEnum.Starter && (
                  <div className={classes.formSection} style={isMobile ? undefined : { minHeight: "514.5px" }}>
                    <div className={classes.sectionHeader}>
                      <p className={classes.sectionTitle}>
                        Main Directory Banners
                      </p>
                    </div>
                    <p className={classes.sectionHelperText}>
                      Images and video banners uploaded here will appear on the Main Directory Banner in the Chamber Perks App™. Use these banners to showcase your chamber, events, and initiatives.
                    </p>
                    <p className={classes.sectionHelperText} style={{ marginTop: 8 }}>
                      Chambers using the Chamber Member Pro™ Platform will also benefit from having their banners appear in the Chamber Directory on their website.
                    </p>
                    <p className={classes.bannerHelperText}>
                      Upload up to 4 images or video banners to promote your profile. An aspect ratio of 16:9 and a minimum size of 1280x720 are recommended for best quality.
                    </p>

                    <div style={{ position: "relative" }}>
                      <div className={classes.bannersGrid} style={subscriptionPlanTypeData?.getChamber?.subscriptionPlanType === ChamberPerksAppSubscriptionPlanTypesEnum.Starter ? { opacity: 0.5, pointerEvents: "none" } : {}}>
                        {directoryBannersData.map((banner, index) => (
                          <div key={index} style={{ display: "flex", width: isMobile ? "100%" : "209px", flexDirection: "column", alignItems: "center", gap: isMobile ? "8px" : "16px", minWidth: 0 }}>
                            <div style={{ textAlign: "center", fontWeight: 600, color: theme.palette.primary.main, fontSize: isMobile ? "15px" : "20px", lineHeight: "24px" }}>
                              Banner {index + 1}
                            </div>
                            {banner.image || banner.videoUrl ? (
                              <div className={classes.bannerCard}>
                                <div className={classes.bannerImageContainer}>
                                  {banner.image ? (
                                    <img
                                      src={banner.image}
                                      alt={`Banner ${index + 1}`}
                                      className={classes.bannerImage}
                                    />
                                  ) : (
                                    <div className={classes.videoPlaceholder}>
                                      <img src={PlayVideoIcon} alt="Play" style={{ width: "36px", height: "36px" }} />
                                      <span style={{ fontSize: "14px", fontWeight: 600, color: theme.palette.primary.main }}>Watch Video</span>
                                    </div>
                                  )}
                                </div>
                                <div className={classes.bannerActions}>
                                  <div className={classes.editDeleteButtons}>
                                    <IconButton
                                      size="small"
                                      className={classes.editButton}
                                      onClick={() => handleOpenDirectoryBannerModal(index + 1)}
                                    >
                                      <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px" }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      className={classes.trashButton}
                                      onClick={() => handleDeleteDirectoryBanner(index)}
                                    >
                                      <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "22px", filter: "brightness(0) invert(1)" }} />
                                    </IconButton>
                                  </div>
                                  <div className={classes.arrowButtons}>
                                    <IconButton
                                      size="small"
                                      className={classes.arrowButton}
                                      onClick={() => handleMoveDirectoryBanner(index, 'left')}
                                      disabled={index === 0}
                                    >
                                      <ArrowBackIosIcon style={{ fontSize: "12px", marginLeft: "4px" }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      className={classes.arrowButton}
                                      onClick={() => handleMoveDirectoryBanner(index, 'right')}
                                      disabled={index === directoryBannersData.length - 1}
                                    >
                                      <ArrowForwardIosIcon style={{ fontSize: "12px" }} />
                                    </IconButton>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleOpenDirectoryBannerModal(index + 1)}
                                className={classes.bannerCardEmpty}
                              >
                                <img
                                  src={UploadRoundIcon}
                                  alt="Upload"
                                  style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px" }}
                                />
                                <p style={{
                                  color: theme.palette.primary.main,
                                  fontSize: isMobile ? "12px" : "20px",
                                  fontWeight: 600,
                                  margin: 0,
                                  lineHeight: "1.3",
                                  textAlign: "center",
                                }}>
                                  Upload Banner
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Footer Text */}
                      <p style={{
                        textAlign: "center",
                        fontSize: isMobile ? "14px" : "16px",
                        fontWeight: 400,
                        lineHeight: isMobile ? "20px" : "24px",
                        color: "#000000",
                        marginTop: "20px",
                        marginBottom: "15px",
                      }}>
                        After previewing your banners, click <span style={{ fontWeight: 700 }}>UPDATE</span> at bottom of page to upload them to the Chamber Perks App™.
                      </p>

                      {/* Preview Banners Button for Directory */}
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                          className={classes.previewBannersButton}
                          onClick={handlePreviewDirectoryBanners}
                          disabled={!directoryBannersData.some(b => b.image || b.videoUrl)}
                          style={directoryBannersData.some(b => b.image || b.videoUrl) ? { marginTop: 0, backgroundColor: "#000000" } : { marginTop: 0 }}
                        >
                          {enumStrings.common.previewBanners}
                        </Button>
                      </div>
                      {subscriptionPlanTypeData?.getChamber
                        ?.subscriptionPlanType ===
                        ChamberPerksAppSubscriptionPlanTypesEnum.Starter && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              cursor: "pointer",
                            }}
                            onClick={handlePlanLimitationModalOpen}
                          />
                        )}
                    </div>
                  </div>
                  )}

                  {userType === UserType.RootAdmin && (
                    <div
                      style={{
                        display: "flex",
                        margin: "auto",
                        alignItems: "baseline",
                        marginTop: "35px",
                      }}
                    >
                      <div
                        className={classes.companyInputWithToolTipContainer}
                        style={{ marginRight: "10px" }}
                      >
                        <h1 style={{ margin: 0 }}>
                          {enumStrings.createChamberText.hideChamberPerksApp}
                        </h1>
                        <ShowInAppSwitch
                          checked={formik.values.hideInApp ? true : false}
                          onChange={(e, checked) => {
                            formik.setFieldValue("hideInApp", checked);
                          }}
                          id="hideInAppToggle"
                          name="hideInAppToggle"
                        />
                      </div>
                    </div>
                  )}

                  {userType === UserType.RootAdmin &&
                    !loadingPremiumChamberMarketerLinkData ? (
                    <div className={classes.companyInputWithToolTipContainer}>
                      <TextField
                        type="text"
                        label={"Premium Chamber Marketer Dropbox Folder Link"}
                        className={classes.companyInput}
                        variant="outlined"
                        inputProps={{ autoComplete: "new-password" }}
                        id="premiumChamberMarketerLink"
                        name="premiumChamberMarketerLink"
                        value={formik.values.premiumChamberMarketerLink}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.premiumChamberMarketerLink &&
                          Boolean(formik.errors.premiumChamberMarketerLink)
                        }
                        helperText={
                          formik.touched.premiumChamberMarketerLink &&
                          formik.errors.premiumChamberMarketerLink
                        }
                      />
                    </div>
                  ) : null}

                  <div>
                    {invalidError ? (
                      <div className={classes.invalidError}>{customError}</div>
                    ) : null}
                  </div>
                </>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", gap: isMobile ? "12px" : "25px", marginTop: isMobile ? "32px" : "-27px", position: "relative", zIndex: 1, padding: isMobile ? "0 16px" : 0 }}>
                  <Button
                    type="submit"
                    size="large"
                    color="primary"
                    variant="contained"
                    className={classes.updateButton}
                    disabled={buttonDisabled || loader}
                  >
                    {loader ? (
                      <CircularProgress style={{ color: "white" }} size={24} />
                    ) : (
                      "UPDATE"
                    )}
                  </Button>
                  {locationStateChamberId ? (
                    <Button
                      size="large"
                      color="secondary"
                      variant="contained"
                      style={{
                        backgroundColor: "#FF0000",
                        fontSize: isMobile ? "16px" : "18px",
                        fontWeight: "bold",
                        borderRadius: "25px",
                        padding: isMobile ? "12px 32px" : "12px 40px",
                        marginLeft: 0,
                        textTransform: "none",
                        width: isMobile ? "100%" : undefined,
                        maxWidth: isMobile ? "320px" : undefined,
                      }}
                      onClick={handleOpen}
                    >
                      {enumStrings.deleteText}
                    </Button>
                  ) : null}
                </div>
              </>
            ) : (
              <Loader />
            )}
          </form>
        </div>
      </div>
      {chamberChangeAdminConsoleLoginEmailModalIsOpen && (
        <ChamberChangeEmailUsernameModal
          chamberId={chamberInfo?.id}
          open={chamberChangeAdminConsoleLoginEmailModalIsOpen}
          handleClose={closeChamberChangeAdminConsoleLoginEmailModal}
          chamberName={chamberInfo?.name || ""}
          currentAdminConsoleLoginEmail={
            adminConsoleLoginEmailForChamber.data
              ?.getAdminConsoleLoginEmailForChamber.adminConsoleLoginEmail
          }
        />
      )}
      {showPlanLimitationModal && (
        <PlanLimitationModal
          open={showPlanLimitationModal}
          handleClose={handlePlanLimitationModalClose}
          chamberName={chamberInfo?.name || ""}
        />
      )}
      {/* Profile Banner Upload Modal */}
      <BannerUploadModal
        open={profileBannerModalOpen}
        onClose={() => setProfileBannerModalOpen(false)}
        bannerIndex={currentProfileBannerIndex}
        initialData={profileBannersData[currentProfileBannerIndex - 1] || {}}
        onSave={handleSaveProfileBanner}
      />
      {/* Directory Banner Upload Modal */}
      <BannerUploadModal
        open={directoryBannerModalOpen}
        onClose={() => setDirectoryBannerModalOpen(false)}
        bannerIndex={currentDirectoryBannerIndex}
        initialData={directoryBannersData[currentDirectoryBannerIndex - 1] || {}}
        onSave={handleSaveDirectoryBanner}
      />
      {/* Delete Banner Confirmation Modal */}
      <DeleteBannerModal
        open={deleteBannerModalOpen}
        onClose={() => {
          setDeleteBannerModalOpen(false);
          setPendingDeleteBanner(null);
        }}
        onDelete={confirmDeleteBanner}
      />
      {/* IDA Generate Description Dialog */}
      <IDAGenerateModal
        open={idaDialogOpen}
        onClose={() => setIdaDialogOpen(false)}
        keyDetails={idaKeyDetails}
        onKeyDetailsChange={setIdaKeyDetails}
        entityName={formik.values.name || "the chamber"}
        entityType="chamber"
        customTitle={enumStrings.ChamberAdminManage.idaChamberTitle}
        customSubtitle={enumStrings.ChamberAdminManage.idaChamberSubtitle}
        onGenerate={(generated) => {
          setIdaGeneratedText(generated);
          setIdaDialogOpen(false);
          setOverwriteDialogOpen(true);
        }}
      />

      {/* IDA Overwrite Confirmation Dialog */}
      <IDAOverwriteDialog
        open={overwriteDialogOpen}
        onClose={() => setOverwriteDialogOpen(false)}
        onRegenerate={() => {
          setOverwriteDialogOpen(false);
          setIdaDialogOpen(true);
        }}
        onConfirm={() => {
          formik.setFieldValue("description", idaGeneratedText);
          setOverwriteDialogOpen(false);
          setDescriptionGeneratedByIda(true);
          setIdaSuccessDialogOpen(true);
          setTimeout(() => setIdaSuccessDialogOpen(false), 5000);
        }}
        title={enumStrings.idaGenerate.chamberOverwriteTitle}
        description={enumStrings.idaGenerate.chamberOverwriteDesc}
        generatedLabel={enumStrings.idaGenerate.chamberGeneratedLabel}
        generatedText={idaGeneratedText}
      />

      {/* IDA Success Dialog */}
      <SuccessDialog
        open={idaSuccessDialogOpen}
        onClose={() => setIdaSuccessDialogOpen(false)}
        message={`${enumStrings.idaGenerate.chamberSuccessTitle}\n${enumStrings.idaGenerate.successTitle2}`}
        autoDismissMs={5000}
      />

      {/* Banner Preview Modal */}
      <BannerPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        banners={previewBannersList}
      />

      {/* Delete Logo Confirmation Modal */}
      <DeleteLogoModal
        open={deleteLogoConfirmationOpen}
        onClose={cancelDeleteLogo}
        onDelete={confirmDeleteLogo}
        title="Delete Logo"
        confirmText="Are you sure you want to delete this logo?"
        description="This action cannot be undone."
      />

      {/* Delete Sponsor Logo Confirmation Modal */}
      <DeleteLogoModal
        open={deleteSponsorLogoConfirmationOpen}
        onClose={cancelDeleteSponsorLogo}
        onDelete={confirmDeleteSponsorLogo}
        title="Delete Sponsor Logo"
        confirmText="Are you sure you want to delete this sponsor logo?"
        description="This action cannot be undone."
      />

      {/* Update Success Dialog */}
      <SuccessDialog
        open={updateSuccessOpen}
        onClose={() => setUpdateSuccessOpen(false)}
        message={enumStrings.ChamberAdminManage.profileUpdatedSuccessfully}
        autoDismissMs={3000}
      />

      {/* Banner Deleted Success Dialog */}
      <SuccessDialog
        open={bannerDeletedSuccessOpen}
        onClose={() => setBannerDeletedSuccessOpen(false)}
        message={enumStrings.bannerUpload.bannerDeletedSuccessfully}
        autoDismissMs={3000}
      />
    </div>
  );
};

export default ManageChamberAsChamberAdmin;
